import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Tag,
  Tooltip,
  Popconfirm,
  message,
  Card,
  Avatar,
  Space,
  Empty,
  Badge,
} from "antd";
import {
  ApiOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  PoweroffOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import UserAPI from "../../utils/api/UserAPI";
import {
  BRAND,
  ghostBtn,
  dangerBtn,
  injectButtonHoverStyles,
} from "./buttonStyles";

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).replace(/@.*/, "").split(/[.\s_]+/).filter(Boolean);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

const elapsedSince = (date) => {
  if (!date) return "-";
  const ms = Date.now() - new Date(date).getTime();
  if (ms < 0) return "-";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

const ActiveSessionsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await UserAPI.getActiveSessions();
      if (res && res.data && res.data.status) {
        setData(res.data.data || []);
      } else {
        message.error((res && res.data && res.data.message) || "Failed to load");
      }
    } catch (e) {
      message.error("Unable to fetch active sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    injectButtonHoverStyles();
    fetch();
  }, []);

  const handleForceSignOut = async (userId) => {
    if (!userId) return;
    try {
      await UserAPI.releaseUserSession(userId);
      message.success("User signed out");
      fetch();
    } catch (e) {
      message.error("Failed to force sign-out");
    }
  };

  const columns = [
    {
      title: "User",
      dataIndex: "username",
      key: "username",
      width: 280,
      render: (v) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Badge dot status="success" offset={[-4, 32]}>
            <Avatar
              size={36}
              style={{
                background: BRAND,
                fontSize: 13,
                textTransform: "uppercase",
                flexShrink: 0,
              }}
            >
              {initials(v)}
            </Avatar>
          </Badge>
          <span style={{ fontWeight: 500, wordBreak: "break-all", lineHeight: 1.3 }}>
            {v || "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Roles",
      dataIndex: "role",
      key: "role",
      width: 200,
      render: (v) =>
        Array.isArray(v) && v.length ? (
          <Space size={[4, 4]} wrap>
            {v.map((r) => (
              <Tag
                key={r}
                style={{
                  borderRadius: 10,
                  color: BRAND,
                  background: "#66339914",
                  border: "1px solid #66339933",
                  marginInlineEnd: 0,
                }}
              >
                {r}
              </Tag>
            ))}
          </Space>
        ) : (
          "-"
        ),
    },
    {
      title: "Signed in",
      dataIndex: "login_at",
      key: "login_at",
      render: (v) =>
        v ? (
          <div>
            <div style={{ fontWeight: 500 }}>{new Date(v).toLocaleString()}</div>
            <Space size={4} style={{ fontSize: 12, color: "#8c8c8c" }}>
              <ClockCircleOutlined />
              {elapsedSince(v)} ago
            </Space>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      render: (v) =>
        v ? <span style={{ fontFamily: "monospace", fontSize: 13 }}>{v}</span> : "-",
    },
    {
      title: "Location",
      key: "geo",
      render: (_, row) => {
        const g = row.geo || {};
        const parts = [g.city, g.region, g.country].filter(Boolean);
        if (!parts.length) return <span style={{ color: "#bfbfbf" }}>Unknown</span>;
        return (
          <Space size={4}>
            <EnvironmentOutlined style={{ color: BRAND }} />
            <span>{parts.join(", ")}</span>
          </Space>
        );
      },
    },
    {
      title: "Device",
      dataIndex: "user_agent",
      key: "user_agent",
      ellipsis: true,
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span style={{ maxWidth: 200, display: "inline-block", color: "#595959" }}>
              {v.slice(0, 36)}…
            </span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, row) => (
        <Popconfirm
          title="Force sign this user out?"
          description="Their session will be cleared immediately."
          okText="Sign out"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleForceSignOut(row.user_id)}
        >
          <Button
            danger
            icon={<PoweroffOutlined />}
            disabled={!row.user_id}
            style={dangerBtn}
          >
            Sign out
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "24px 28px",
        marginTop: "70px",
        marginLeft: 8,
        background: "#f5f6f8",
        minHeight: "calc(100vh - 70px)",
        width: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: BRAND,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            <ApiOutlined />
          </div>
          <div>
            <h3 style={{ margin: 0, color: BRAND, fontWeight: 700 }}>
              Active Sessions{" "}
              <Badge
                count={data.length}
                showZero
                style={{ background: "#52c41a", marginLeft: 6 }}
              />
            </h3>
            <span style={{ color: "#8c8c8c", fontSize: 13 }}>
              Users currently signed in — force sign-out clears their session
            </span>
          </div>
        </div>
        <Button
          className="sl-btn-ghost"
          icon={<ReloadOutlined />}
          onClick={fetch}
          style={ghostBtn}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 8 }}
      >
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={data}
          columns={columns}
          size="middle"
          scroll={{ x: 1000, y: "calc(100vh - 320px)" }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No active sessions right now"
              />
            ),
          }}
          pagination={{ pageSize: 25, showTotal: (t) => `${t} active` }}
        />
      </Card>
    </div>
  );
};

export default ActiveSessionsTable;
